import React from "react";
import Link from "next/link";
import Head from "next/head";
import PurpleDoorLayout from "@/components/purpledoor/PurpleDoorLayout";

const ENABLED = process.env.NEXT_PUBLIC_PURPLEDOOR_ENABLED === "true";

const features = [
    {
        title: "Voice-First Conversation",
        description:
            "Speak naturally about what you need. Our AI listens, understands, and guides you — no forms or filters required.",
        icon: (
            <svg className="w-7 h-7" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 18.75a6 6 0 006-6v-1.5m-6 7.5a6 6 0 01-6-6v-1.5m6 7.5v3.75m-3.75 0h7.5M12 15.75a3 3 0 01-3-3V4.5a3 3 0 116 0v8.25a3 3 0 01-3 3z" />
            </svg>
        ),
    },
    {
        title: "Learns What You Want",
        description:
            "The more you share, the smarter the search gets. Like or pass on communities and watch results improve in real time.",
        icon: (
            <svg className="w-7 h-7" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09zM18.259 8.715L18 9.75l-.259-1.035a3.375 3.375 0 00-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 002.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 002.455 2.456L21.75 6l-1.036.259a3.375 3.375 0 00-2.455 2.456zM16.894 20.567L16.5 21.75l-.394-1.183a2.25 2.25 0 00-1.423-1.423L13.5 18.75l1.183-.394a2.25 2.25 0 001.423-1.423l.394-1.183.394 1.183a2.25 2.25 0 001.423 1.423l1.183.394-1.183.394a2.25 2.25 0 00-1.423 1.423z" />
            </svg>
        ),
    },
    {
        title: "40,000+ Communities",
        description:
            "Every senior living option in one place — independent living, assisted living, memory care, and more. No commissions, no pressure.",
        icon: (
            <svg className="w-7 h-7" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 12l8.954-8.955c.44-.439 1.152-.439 1.591 0L21.75 12M4.5 9.75v10.125c0 .621.504 1.125 1.125 1.125H9.75v-4.875c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125V21h4.125c.621 0 1.125-.504 1.125-1.125V9.75M8.25 21h8.25" />
            </svg>
        ),
    },
];

const steps = [
    { num: "1", text: "Tell us who you're searching for and what matters most." },
    { num: "2", text: "Our AI finds communities that match — and explains why." },
    { num: "3", text: "React to results: like, pass, or save favorites." },
    { num: "4", text: "Watch the search get smarter with every interaction." },
];

export default function PurpleDoorLanding() {
    if (!ENABLED) {
        return (
            <PurpleDoorLayout>
                <div className="flex items-center justify-center min-h-[60vh]">
                    <p className="text-gray-500">This feature is not yet available.</p>
                </div>
            </PurpleDoorLayout>
        );
    }

    return (
        <PurpleDoorLayout>
            <Head>
                <title>AI-Powered Senior Living Search | Purple Door Finders</title>
                <meta
                    name="description"
                    content="Find the right senior living community through a natural voice conversation. Our AI guide helps you discover assisted living, memory care, and independent living options — commission-free."
                />
                <meta property="og:title" content="AI-Powered Senior Living Search | Purple Door Finders" />
                <meta
                    property="og:description"
                    content="Voice-first AI search for senior living. Speak naturally, get personalized results from 40,000+ communities."
                />
                <meta property="og:url" content="https://purpledoorfinders.com/purpledoor" />
                <link rel="canonical" href="https://purpledoorfinders.com/purpledoor" />
            </Head>

            {/* Hero */}
            <section className="relative overflow-hidden bg-gradient-to-br from-primary-600 via-primary-500 to-primary-700">
                <div className="absolute inset-0 opacity-10">
                    <div className="absolute -top-24 -right-24 w-96 h-96 bg-white rounded-full blur-3xl" />
                    <div className="absolute bottom-0 left-0 w-80 h-80 bg-secondary-400 rounded-full blur-3xl" />
                </div>
                <div className="relative max-w-5xl mx-auto px-4 sm:px-6 py-20 sm:py-32 text-center">
                    <h1 className="text-3xl sm:text-5xl font-headline font-bold text-white leading-tight">
                        Find Senior Living<br className="hidden sm:block" />{" "}
                        Through Conversation
                    </h1>
                    <p className="mt-5 text-lg sm:text-xl text-primary-100 max-w-2xl mx-auto leading-relaxed">
                        Most people searching for senior living don&apos;t know exactly what
                        they need. Our AI guide listens, learns, and helps you discover the
                        right community — at your own pace.
                    </p>
                    <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-4">
                        <Link href="/purpledoor/search" className="w-full sm:w-auto px-8 py-3.5 text-base font-semibold text-primary-600 bg-white rounded-lg shadow-lg hover:shadow-xl hover:bg-gray-50 transition-all">
                                Start Your Search
                        </Link>
                        <a
                            href="#how-it-works"
                            className="w-full sm:w-auto px-8 py-3.5 text-base font-semibold text-white border-2 border-white/40 rounded-lg hover:border-white/70 transition-colors text-center"
                        >
                            See How It Works
                        </a>
                    </div>
                    <p className="mt-6 text-sm text-primary-200">
                        Free to use &middot; No commissions &middot; 40,000+ communities
                    </p>
                </div>
            </section>

            {/* Features */}
            <section className="py-16 sm:py-24 bg-white">
                <div className="max-w-5xl mx-auto px-4 sm:px-6">
                    <h2 className="text-2xl sm:text-3xl font-headline font-bold text-gray-900 text-center">
                        A Better Way to Search
                    </h2>
                    <p className="mt-3 text-gray-600 text-center max-w-2xl mx-auto">
                        No filters to figure out. No endless scrolling. Just a
                        conversation about what matters to you and your family.
                    </p>
                    <div className="mt-14 grid sm:grid-cols-3 gap-10">
                        {features.map((f) => (
                            <div key={f.title} className="text-center sm:text-left">
                                <div className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-primary-50 text-primary-500 mb-4">
                                    {f.icon}
                                </div>
                                <h3 className="text-lg font-bold text-gray-900">{f.title}</h3>
                                <p className="mt-2 text-gray-600 leading-relaxed text-sm">
                                    {f.description}
                                </p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* How It Works */}
            <section id="how-it-works" className="py-16 sm:py-24 bg-gray-50">
                <div className="max-w-3xl mx-auto px-4 sm:px-6">
                    <h2 className="text-2xl sm:text-3xl font-headline font-bold text-gray-900 text-center">
                        How It Works
                    </h2>
                    <div className="mt-12 space-y-8">
                        {steps.map((s) => (
                            <div key={s.num} className="flex gap-5 items-start">
                                <span className="flex-shrink-0 flex items-center justify-center w-10 h-10 rounded-full bg-primary-500 text-white font-bold text-sm">
                                    {s.num}
                                </span>
                                <p className="text-gray-700 text-base pt-1.5 leading-relaxed">
                                    {s.text}
                                </p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* CTA */}
            <section className="py-16 sm:py-20 bg-white">
                <div className="max-w-3xl mx-auto px-4 sm:px-6 text-center">
                    <h2 className="text-2xl sm:text-3xl font-headline font-bold text-gray-900">
                        Ready to find the right community?
                    </h2>
                    <p className="mt-3 text-gray-600 max-w-lg mx-auto">
                        It takes just a few minutes to get started. No pressure, no
                        obligations — just a helpful conversation.
                    </p>
                    <Link href="/purpledoor/search" className="inline-block mt-8 px-10 py-3.5 text-base font-semibold text-white bg-primary-500 rounded-lg shadow-md hover:bg-primary-400 hover:shadow-lg transition-all">
                            Start Your Search
                    </Link>
                </div>
            </section>
        </PurpleDoorLayout>
    );
}

export async function getStaticProps() {
    return {
        props: {
            meta: {
                title: "AI-Powered Senior Living Search | Purple Door Finders",
                description:
                    "Find the right senior living community through a natural voice conversation with our AI guide. Commission-free, 40,000+ communities.",
            },
        },
    };
}
